import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Deposit, Fund, Valuation } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { computeBatchPreview } from '../../calc/preview'
import { InfoHint } from '../../components/InfoHint'
import { SectionCard } from '../../components/SectionCard'
import { tokens } from '../../theme'
import { formatCzk, formatDate, formatUnits, todayIso } from './format'

/** Malý odznak „override" u ručně upravené ceny/jednotek (vzor ManualBadge z Platby). */
function OverrideChip() {
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '9999px',
        fontWeight: 600,
        fontSize: '0.6875rem',
        px: '6px',
        py: '1px',
        bgcolor: tokens.warning[100],
        color: tokens.warning[700],
      }}
    >
      override
    </Box>
  )
}

interface BatchPreviewCardProps {
  fund: Fund
  latestValuation: Valuation | undefined
  selectedDeposits: Deposit[]
  overrides: Record<string, { priceOverride?: number; unitsOverride?: number }>
  onEditOverride: (depositId: string) => void
  onCreated: () => void
}

/**
 * Náhled přepočtu: cena z ocenění, jednotky per účastník (vč. overridů), total po.
 * Veškerá matematika jde přes computeBatchPreview — UI jen zobrazuje výsledek.
 */
export function BatchPreviewCard({
  fund,
  latestValuation,
  selectedDeposits,
  overrides,
  onEditOverride,
  onCreated,
}: BatchPreviewCardProps) {
  const api = useOpsApi()
  const [assignmentDate, setAssignmentDate] = useState<string>(todayIso)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useMemo(() => {
    if (!latestValuation || selectedDeposits.length === 0) return null
    return computeBatchPreview({
      totalUnitsBefore: fund.totalUnits,
      unitPrice: latestValuation.unitPrice,
      participants: selectedDeposits.map((d) => ({
        depositId: d.id,
        clientId: d.clientId,
        clientName: d.clientName,
        amount: d.amountReceived ?? d.amount,
        ...(overrides[d.id] ?? {}),
      })),
    })
  }, [fund.totalUnits, latestValuation, selectedDeposits, overrides])

  const handleSubmit = async () => {
    if (!latestValuation) return
    setSubmitting(true)
    setError(null)
    try {
      await api.createBatch({
        fundKey: fund.key,
        valuationId: latestValuation.id,
        assignmentDate,
        participants: selectedDeposits.map((d) => ({
          depositId: d.id,
          ...(overrides[d.id] ?? {}),
        })),
      })
      onCreated()
    } catch (err) {
      setError(apiError(err, 'Vytvoření přepočtu selhalo'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SectionCard
      title="Náhled přepočtu"
      info={
        <InfoHint
          title="Vzorec náhledu"
          lines={[
            'Jednotky účastníka = přijatá částka / cena jednotky z vybraného ocenění (8 desetinných míst).',
            'Případný override ceny/jednotek má přednost a projde kontrolou ve fázi Kontrola.',
            'Do potvrzení je přepočet DRAFT — jednotky klientům zatím připsané nejsou.',
          ]}
          sx={{ color: '#fff' }}
        />
      }
    >
      {!latestValuation ? (
        <Alert severity="info">
          Nejdřív vytvoř ocenění fondu — bez ceny jednotky nelze přepočet spustit.
        </Alert>
      ) : !preview ? (
        <Typography variant="body2" color="text.secondary">
          Vyber účastníky ve frontě výše.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Ocenění z {formatDate(latestValuation.date)}: NAV {formatCzk(latestValuation.navTotal)}
              , cena jednotky {formatUnits(latestValuation.unitPrice)}
            </Typography>
            <TextField
              size="small"
              type="date"
              label="Datum přidělení"
              value={assignmentDate}
              onChange={(e) => setAssignmentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={submitting}
              sx={{ width: 200 }}
            />
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Klient</TableCell>
                  <TableCell align="right">Vklad</TableCell>
                  <TableCell align="right">Cena</TableCell>
                  <TableCell align="right">Jednotky</TableCell>
                  <TableCell align="right">Úprava</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.participants.map((p) => (
                  <TableRow key={p.depositId}>
                    <TableCell>{p.clientName}</TableCell>
                    <TableCell align="right">{formatCzk(p.amount)}</TableCell>
                    <TableCell align="right">
                      {formatUnits(p.price)}
                      {p.priceOverride != null && <OverrideChip />}
                    </TableCell>
                    <TableCell align="right">
                      {formatUnits(p.units)}
                      {p.unitsOverride != null && <OverrideChip />}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => onEditOverride(p.depositId)}
                        disabled={submitting}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">
              Σ přidělených jednotek: <b>{formatUnits(preview.unitsAssigned)}</b>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Celkem jednotek: před {formatUnits(preview.totalUnitsBefore)} → po{' '}
              {formatUnits(preview.totalUnitsAfter)}
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              Vytvořit přepočet (DRAFT)
            </Button>
          </Box>
        </Box>
      )}
    </SectionCard>
  )
}
